import { useEffect, useState } from 'react';
import {
  ArtEventInterface,
  UpdateArtEventRequest,
  UpdateArtEventResponse,
} from '../interfaces/editArtEventInterfaces';
import { useGetArtEvents } from './useGetArtEvents';
import { useDeleteArtEventMutation } from './useDeleteArtEventMutation';
import { useFreezeArtEventMutation } from './useFreezeArtEventMutation';
import { useActivateArtEventMutation } from './useActivateArtEventMutation';
import { ART_EVENT_STATUS } from '../constants/artEventStatus';
import { DEFAULT_ART_EVENT_WAITLIST } from '../constants/artEventWaitlist';
import { MODAL_CONFIGS } from '../config/modalConfig';
import { ACTION_TYPES } from '../constants/actionTypes';
import { UseMutationResult } from '@tanstack/react-query';
import { ApiError } from '../../../features/Shared/interfaces/ApiError';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SIDE_NAVIGATION_RESET_PARAM } from '../../../features/Shared/constants/commonConstants';
import { URLS } from '../../../features/Shared/constants/url';

export const useEditArtEvent = () => {
  const { data, isLoading, isError, error, refetch } = useGetArtEvents();
  const deleteMutation = useDeleteArtEventMutation();
  const freezeMutation = useFreezeArtEventMutation();
  const activateMutation = useActivateArtEventMutation();

  const [selectedArtEvent, setSelectedArtEvent] =
    useState<ArtEventInterface | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  // Snackbar tone: success messages stay green; failures (e.g. a rejected waitlist change) show red.
  const [snackbarIsError, setSnackbarIsError] = useState<boolean>(false);
  const [freezeModal, setFreezeModal] = useState<boolean>(false);
  const [artEventToFreeze, setArtEventToFreeze] =
    useState<ArtEventInterface | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [artEventToDelete, setArtEventToDelete] =
    useState<ArtEventInterface | null>(null);
  const [activateModal, setActivateModal] = useState<boolean>(false);
  const [artEventToActivate, setArtEventToActivate] =
    useState<ArtEventInterface | null>(null);

  const artEventsData: ArtEventInterface[] =
    data?.artEvents
      ?.filter((event) => event.status !== ART_EVENT_STATUS.DELETED)
      .map((event) => ({
        artEventID: event.artEventID,
        startTime: event.startTimestamp,
        endTime: event.endTimestamp,
        status: event.status,
        waitlistEnabled: event.waitlistEnabled ?? DEFAULT_ART_EVENT_WAITLIST,
        waitlistFeatureEnabled: event.features?.waitlist ?? false,
      })) || [];

  const actionTypeConfig: Record<
    string,
    {
      mutation: UseMutationResult<
        UpdateArtEventResponse | null,
        Error,
        UpdateArtEventRequest
      >;
      artEvent: ArtEventInterface | null;
      setModal: (value: boolean) => void;
      setArtEvent: (value: ArtEventInterface | null) => void;
      isOpen: boolean;
    }
  > = {
    [ACTION_TYPES.FREEZE]: {
      mutation: freezeMutation,
      artEvent: artEventToFreeze,
      setModal: setFreezeModal,
      setArtEvent: setArtEventToFreeze,
      isOpen: freezeModal,
    },
    [ACTION_TYPES.ACTIVATE]: {
      mutation: activateMutation,
      artEvent: artEventToActivate,
      setModal: setActivateModal,
      setArtEvent: setArtEventToActivate,
      isOpen: activateModal,
    },
  };

  const handleConfirm = (type: string) => {
    const config = MODAL_CONFIGS.find(
      (modalConfig) => modalConfig.type === type
    );

    if (!config) return;

    if (type === ACTION_TYPES.DELETE && artEventToDelete) {
      deleteMutation.mutate(artEventToDelete.artEventID, {
        onSuccess: () => {
          setSnackbarIsError(false);
          setSnackbarMessage(config.successMessageKey);
          setDeleteModal(false);
          setArtEventToDelete(null);
        },
        onError: (error: Error) => {
          setSnackbarIsError(true);
          setSnackbarMessage(error?.message || config.errorMessageKey);
          setDeleteModal(false);
          setArtEventToDelete(null);
        },
      });
      return;
    }
    const typeConfig = actionTypeConfig[type];
    if (typeConfig?.artEvent && config) {
      typeConfig.mutation.mutate(
        {
          artEvent: {
            artEventID: typeConfig.artEvent.artEventID,
            startTimestamp: typeConfig.artEvent.startTime,
            endTimestamp: typeConfig.artEvent.endTime,
            status: config.status,
          },
        },
        {
          onSuccess: () => {
            setSnackbarIsError(false);
            setSnackbarMessage(config.successMessageKey);
            typeConfig.setModal(false);
            typeConfig.setArtEvent(null);
          },
          onError: (error: Error) => {
            setSnackbarIsError(true);
            setSnackbarMessage(error?.message || config.errorMessageKey);
            typeConfig.setModal(false);
            typeConfig.setArtEvent(null);
          },
        }
      );
    }
  };

  const handleDataTableAction = (
    actionType: string,
    row: ArtEventInterface
  ) => {
    if (actionType === ACTION_TYPES.EDIT) {
      setSelectedArtEvent(row);
    }
    if (actionType === ACTION_TYPES.FREEZE) {
      setArtEventToFreeze(row);
      setFreezeModal(true);
    }
    if (actionType === ACTION_TYPES.DELETE) {
      setArtEventToDelete(row);
      setDeleteModal(true);
    }
    if (actionType === ACTION_TYPES.ACTIVATE) {
      setArtEventToActivate(row);
      setActivateModal(true);
    }
  };

  const getModalProps = (type: string) => {
    if (type === ACTION_TYPES.DELETE) {
      return {
        isOpen: deleteModal,
        artEvent: artEventToDelete,
        isLoading: deleteMutation.isPending,
        onConfirm: () => handleConfirm(type),
        onCancel: () => {
          setDeleteModal(false);
          setArtEventToDelete(null);
        },
      };
    }
    const typeConfig = actionTypeConfig[type];
    return {
      isOpen: typeConfig?.isOpen || false,
      artEvent: typeConfig?.artEvent || null,
      isLoading: typeConfig?.mutation.isPending || false,
      onConfirm: () => handleConfirm(type),
      onCancel: () => {
        if (typeConfig) {
          typeConfig.setModal(false);
          typeConfig.setArtEvent(null);
        }
      },
    };
  };

  const handleFormClose = () => setSelectedArtEvent(null);

  const handleFormSuccess = (message: string) => {
    setSnackbarIsError(false);
    setSnackbarMessage(message);
    setSelectedArtEvent(null);
    refetch();
  };

  const handleFormError = (message: string) => {
    // Surface the failure instead of silently closing as if it succeeded, and refetch so a stale
    // waitlist value (e.g. the event was advanced by someone else) is corrected in the list.
    setSnackbarIsError(true);
    setSnackbarMessage(message);
    setSelectedArtEvent(null);
    refetch();
  };

  const handleSnackbarClose = () => setSnackbarMessage('');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Reset all form states when reset URL parameter is present
  useEffect(() => {
    if (searchParams.get('reset') === SIDE_NAVIGATION_RESET_PARAM.reset) {
      handleFormClose();

      // Clean URL by removing reset parameter without triggering navigation
      navigate(URLS.ART_EVENT, { replace: true });
    }
  }, [searchParams, navigate]);

  return {
    artEventsData,
    isLoading,
    isError,
    error: error as ApiError,
    selectedArtEvent,
    snackbarMessage,
    snackbarIsError,
    handleDataTableAction,
    getModalProps,
    handleFormClose,
    handleFormSuccess,
    handleFormError,
    handleSnackbarClose,
  };
};
